export const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

export const getQuantileValue = (values: number[], percentile: number) => {
  if (!values.length) {
    return 0;
  }

  const clampedPercentile = Number.isFinite(percentile) ? clamp(percentile, 0, 1) : 0.5;
  const position = (values.length - 1) * clampedPercentile;
  const lowerIndex = Math.floor(position);
  const upperIndex = Math.ceil(position);

  const arr = [...values];
  const quickSelect = (k: number) => {
    let left = 0;
    let right = arr.length - 1;
    let remainingWork = arr.length * 8;

    while (left < right) {
      remainingWork -= right - left + 1;
      if (remainingWork < 0) {
        // Bound the cost of adversarial pivot sequences as well as duplicate data.
        arr.sort((a, b) => a - b);
        return arr[k];
      }
      const first = arr[left];
      const middle = arr[Math.floor((left + right) / 2)];
      const last = arr[right];
      const pivot = Math.max(Math.min(first, middle), Math.min(Math.max(first, middle), last));
      let low = left;
      let high = right;
      while (low <= high) {
        while (arr[low] < pivot) {
          low++;
        }
        while (arr[high] > pivot) {
          high--;
        }
        if (low <= high) {
          const value = arr[low];
          arr[low] = arr[high];
          arr[high] = value;
          // Both cursors advance on equal values, keeping constant samples linear.
          low++;
          high--;
        }
      }
      if (k <= high) {
        right = high;
      } else if (k >= low) {
        left = low;
      } else {
        return arr[k];
      }
    }

    return arr[k];
  };

  const lowerValue = quickSelect(lowerIndex);
  if (lowerIndex === upperIndex) {
    return lowerValue;
  }

  // Selection partitions around lowerIndex; the next order statistic is the
  // smallest value to its right, so a second partition pass is unnecessary.
  let upperValue = arr[upperIndex];
  for (let index = upperIndex + 1; index < arr.length; index++) {
    if (arr[index] < upperValue) {
      upperValue = arr[index];
    }
  }
  const fraction = position - lowerIndex;

  return lowerValue + (upperValue - lowerValue) * fraction;
};

export const median = (values: number[]) => getQuantileValue(values, 0.5);
